import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import CompanyApi from "../../../api/companyApi";
import UsersApi from "../../../api/usersApi";
import { ReactComponent as CloseIcon } from "../../../assets/General/close.svg";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../components/Input/Input";
import MuiSwitch from "../../../components/MuiSwitch/MuiSwitch";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { network } from "../../../config";
import { emptyMultiLangStrings } from "../../../constans/siteLangs";
import { ContextProvider } from "../../../contextProvider";
import { Company } from "../../../types/Company";
import { TypeUserData } from "../../../types/TypeUsers";
import url from "../../../utils/formApiUrl";
import { getTranslation } from "../../../utils/getTranslation";
import {
  resolveSignedReadUrl,
  uploadStoredFileWithPresignedUrl,
} from "../../../utils/storedFileReferences";
import Notify, { SuccesNotify } from "../../../utils/toaster";
import s from "./CreateCompany.module.css";

const connectionString = process.env.REACT_APP_HTTP_CONNECTION_STRING;

const { companies } = network;

type CreateCompanyProps = {
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setReloadCompanyTriger: Dispatch<SetStateAction<number>>;
  isUpdateCompany?: boolean;
  companyData?: Company;
};

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
  { code: "uk", label: "UK" },
];

const CreateCompany = ({
  setModalVisible,
  setPage,
  setReloadCompanyTriger,
  isUpdateCompany,
  companyData,
}: CreateCompanyProps) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const [newCompany, setNewCompany] = useState({
    _id: "", // только при обновлении
    title: "",
    description: emptyMultiLangStrings,
    owner: { _id: "", name: "", surname: "", email: "" },
    isBarterAvailable: false,
  });
  const [searchUsers, setSearchUsers] = useState("");
  const [usersData, setUsersData] = useState<TypeUserData[]>([]);
  const [logo, setLogo] = useState<File | undefined>();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loader, setLoader] = useState(false);
  const [selecLangForText, setSelecLangForText] = useState("en");

  // Инициализация формы при обновлении
  useEffect(() => {
    if (!isUpdateCompany || !companyData) {
      // Сброс формы при создании
      setNewCompany({
        _id: "",
        title: "",
        description: emptyMultiLangStrings,
        owner: { _id: "", name: "", surname: "", email: "" },
        isBarterAvailable: false,
      });
      setLogo(undefined);
      setLogoPreview(null);
      return;
    }

    setNewCompany({
      _id: companyData._id,
      title: companyData.name,
      description: companyData.description?.length
        ? companyData.description
        : emptyMultiLangStrings,
      owner: {
        _id: companyData.owner._id,
        name: getTranslation(companyData.owner.name),
        surname: getTranslation(companyData.owner.surname),
        email: companyData.owner.email,
      },
      isBarterAvailable: companyData.isBarterAvailable || false,
    });

    const resolveLogo = async () => {
      if (companyData.logoS3 && token) {
        const signedLogo = await resolveSignedReadUrl({
          storedFileReference: companyData.logoS3,
          getSignedUrl: (filepath) =>
            CompanyApi.getCompanyLogoS3SignedUrl(
              token,
              companyData._id,
              filepath,
            ),
        });
        setLogoPreview(signedLogo ?? null);
        return;
      }

      if (companyData.logo) {
        setLogoPreview(
          url(`${connectionString}companies/`, companies.getLogoStream, {
            id: companyData.logo,
          }),
        );
      }
    };

    resolveLogo();
  }, [isUpdateCompany, companyData, token]);

  const uploadLogo = async (companyId: string) => {
    if (!logo || !token) return true;
    const logoS3 = await uploadStoredFileWithPresignedUrl({
      file: logo,
      getUploadUrl: (body) =>
        CompanyApi.getCompanyLogoUploadUrl(token, companyId, body),
    });
    const response = await CompanyApi.uploadCompanyLogoS3(
      token,
      companyId,
      logoS3,
    );
    if (!response.status && response.message) {
      Notify(response.message);
      return false;
    }
    return response.status;
  };

  const handleSubmit = async () => {
    if (!token || !userData) return;

    setLoader(true);

    try {
      let response;

      if (isUpdateCompany && newCompany._id) {
        // === РЕЖИМ ОБНОВЛЕНИЯ ===
        const updateData = {
          _id: newCompany._id,
          name: newCompany.title,
          description: newCompany.description,
          owner: newCompany.owner._id,
          isBarterAvailable: newCompany.isBarterAvailable,
          logo: !logoPreview ? null : undefined,
        };

        response = await CompanyApi.update(token, updateData);

        if (response.status && response.company) {
          if (logo) {
            await uploadLogo(response.company._id);
          }
          SuccesNotify("Company updated successfully");
        } else {
          Notify(response.message || "Failed to update company");
          return;
        }
      } else {
        // === РЕЖИМ СОЗДАНИЯ ===
        const payload = {
          userId: userData._id,
          ownerId: newCompany.owner._id,
          newCompany: {
            name: newCompany.title,
            description: newCompany.description,
            isBarterAvailable: newCompany.isBarterAvailable,
          },
        };

        response = await CompanyApi.createCompany(token, payload);

        if (response.status && response.company) {
          if (logo) {
            await uploadLogo(response.company._id);
          }
        } else {
          Notify(response.message || "Failed to create company");
          return;
        }
      }

      // Успешно — закрываем и обновляем список
      setModalVisible(false);
      setPage(0);
      setReloadCompanyTriger((prev) => prev + 1);
    } catch (error) {
      console.error("Error saving company:", error);
      Notify("An error occurred. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogo(undefined);
    setLogoPreview(null);
    const input = document.getElementById("logo-upload") as HTMLInputElement;
    if (input) input.value = "";
  };

  const handleDescriptionChange = (value: string) => {
    setNewCompany((prev) => ({
      ...prev,
      description: prev.description.map((item) =>
        item.language === selecLangForText ? { ...item, text: value } : item,
      ),
    }));
  };

  const getCurrentDescription = () => {
    const desc = newCompany.description.find(
      (item) => item.language === selecLangForText,
    );
    return desc?.text || "";
  };

  useEffect(() => {
    if (newCompany.owner._id.length) {
      setSearchUsers("");
    }
  }, [newCompany.owner._id]);

  useEffect(() => {
    if (!searchUsers.length) {
      setUsersData([]);
      return;
    }

    const makeAsync = async () => {
      if (!token || searchUsers.length < 1) return;

      const response = await UsersApi.findUserBySearchString(
        token,
        searchUsers,
        "false",
      );

      if (response.status && response.users) {
        setUsersData(response.users);
      }
    };
    makeAsync();
  }, [token, searchUsers]);

  const isFormValid =
    newCompany.title.trim().length > 0 &&
    newCompany.owner._id.length > 0 &&
    !loader;

  return (
    <div className={s.container}>
      <h2 className={s.newCompanyTitle}>
        {isUpdateCompany ? "Update company" : "New company"}
      </h2>

      {/* Company Name */}
      <Input
        inputValue={newCompany.title}
        onChangeInput={(value) =>
          setNewCompany((prev) => ({ ...prev, title: value }))
        }
        isVisible
        required
        placeholder="Company..."
        lable="Company name"
      />

      {/* Barter Switch */}
      <div className={s.switchBlock}>
        <span>Enable barter sessions?</span>
        <MuiSwitch
          checked={newCompany.isBarterAvailable}
          setChecked={(value) =>
            setNewCompany((prev) => ({ ...prev, isBarterAvailable: value }))
          }
        />
      </div>

      {/* Description with Language Tabs */}
      <div className={s.descriptionBlock}>
        <span className={s.label}>Description</span>
        <div className={s.langTabs}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`${s.langTab} ${
                selecLangForText === lang.code ? s.active : ""
              }`}
              onClick={() => setSelecLangForText(lang.code)}
            >
              {lang.label}
            </button>
          ))}
        </div>
        <textarea
          className={s.descriptionTextarea}
          value={getCurrentDescription()}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder={`Enter description in ${selecLangForText.toUpperCase()}...`}
          rows={4}
        />
      </div>

      {/* Logo Upload */}
      <div className={s.logoBlock}>
        <span className={s.label}>Company Logo</span>
        <label htmlFor="logo-upload" className={s.uploadLabel}>
          {logoPreview ? (
            <div className={s.logoPreview}>
              <img src={logoPreview} alt="Logo preview" />
              <button
                type="button"
                className={s.removeLogo}
                onClick={(e) => {
                  e.preventDefault();
                  removeLogo();
                }}
              >
                <CloseIcon />
              </button>
            </div>
          ) : (
            <div className={s.uploadPlaceholder}>
              <span>Click to upload logo</span>
              <span className={s.uploadHint}>(JPG, PNG, max 50MB)</span>
            </div>
          )}
        </label>
        <input
          id="logo-upload"
          type="file"
          accept="image/jpeg,image/png"
          onChange={handleLogoChange}
          style={{ display: "none" }}
        />
      </div>

      {/* Owner Selection */}
      <div className={s.selectOwnerBlock}>
        <span>Select owner for company</span>
        <span className={s.searchDescription}>
          (You can search users by name or email)
        </span>
        {newCompany.owner._id.length ? (
          <div className={s.selectedUserBlock}>
            <div className={s.selectedUser}>
              <CircleAvatar
                userId={newCompany.owner._id}
                width="40px"
                height="40px"
              />
              <span>
                {newCompany.owner.name + " " + newCompany.owner.surname}
              </span>
              <span>{newCompany.owner.email}</span>
            </div>
            <CloseIcon
              className={s.closeIcon}
              onClick={() =>
                setNewCompany((prev) => ({
                  ...prev,
                  owner: { _id: "", name: "", surname: "", email: "" },
                }))
              }
            />
          </div>
        ) : (
          <Input
            inputValue={searchUsers}
            onChangeInput={(value) => setSearchUsers(value)}
            placeholder="Search..."
            isVisible
            required
            isSearchinput
          />
        )}
      </div>

      {/* Submit Button & Search Results */}
      <div className={s.createBtnBlock}>
        <StandartButton
          action={handleSubmit}
          buttonTitle={isUpdateCompany ? "Update" : "Create"}
          width="200px"
          disabled={!isFormValid}
          tooltipTitle={
            loader
              ? "Saving..."
              : !isFormValid
                ? "Fill in all required fields"
                : undefined
          }
        />
        {usersData.length ? (
          <div className={s.searchResultBlock}>
            {usersData.map((item, index) => (
              <div
                key={item._id}
                className={
                  index === usersData.length - 1 ? s.lastUserBlock : s.userBlock
                }
                onClick={() =>
                  setNewCompany((prev) => ({
                    ...prev,
                    owner: {
                      _id: item._id,
                      name: getTranslation(item.name!),
                      surname: getTranslation(item.surname!),
                      email: item.email!,
                    },
                  }))
                }
              >
                <CircleAvatar userId={item._id} width="40px" height="40px" />
                <span>
                  {getTranslation(item.name) +
                    " " +
                    getTranslation(item.surname)}
                </span>
                <span>{item.email}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CreateCompany;
